<template>
  <input
    ref="inputRef"
    class="base-input number-input"
    type="number"
    :data-test-id="`${props.testId}__BaseInputNumberNullAllowed`"
    :value="internalValue"
    :step="step"
    :placeholder="placeholder"
    onkeydown="return event.keyCode !== 69"
    @input="inputHandler($event)"
  >
</template>

<script lang="ts" setup>
  import { useDebounceFn, useEventListener } from '@vueuse/core'
  import round from 'lodash/round'
  import { ref, watch } from 'vue'

  import { clamp } from '@/utils/common'

  const props = defineProps({
    debounceAfter: {
      default: 500,
      type: Number,
    },
    max: {
      default: 100,
      type: Number,
    },
    min: {
      default: 0,
      type: Number,
    },
    modelValue: {
      required: false,
      type: [Number, String],
    },
    placeholder: {
      default: '',
      required: false,
      type: String,
    },
    precision: {
      default: 0,
      type: Number,
    },
    step: {
      default: 1,
      type: Number,
    },
    testId: {
      required: true,
      type: String,
    },
  })

  const emit = defineEmits(['update:modelValue', 'inputStatus'])
  const inputRef = ref<HTMLInputElement>()
  const internalValue = ref(props.modelValue)
  const lastValue = ref<string | number | undefined>(0)
  const updateValue = useDebounceFn((event: Event) => {
    lastValue.value = internalValue.value
    const target = event.target as HTMLInputElement
    internalValue.value = target.value

    if (target.value !== '') {
      internalValue.value = clamp(
        round(Number(target.value), props.precision),
        props.min,
        props.max,
      )
    }
    emit('update:modelValue', internalValue.value)
    emit('inputStatus', 'finished')
  }, props.debounceAfter)

  const inputHandler = (event: Event) => {
    emit('inputStatus', 'inprogress')
    void updateValue(event)
  }

  const handleScroll = () => {
    if (inputRef.value) {
      inputRef.value.blur()
    }
  }

  watch(
    () => props.modelValue,
    value => {
      if (value === '') {
        internalValue.value = undefined
        return
      }
      internalValue.value = value
    },
  )

  useEventListener(inputRef, 'wheel', handleScroll)
</script>

<style scoped>
  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    margin: 0;
    appearance: none;
  }

  input[type="number"] {
    appearance: textfield;
  }
</style>
